import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "@/Components/ui/button";

export function OSCCombobox({ value, onValueChange, options = [], placeholder = "Selecione..." }) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const buttonRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Filtrar opções baseado no termo de pesquisa
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) return options;
    const searchLower = searchTerm.toLowerCase();
    return options.filter(option => 
      option.toLowerCase().includes(searchLower)
    );
  }, [options, searchTerm]);

  // Reset highlighted index quando filtrar
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions]);

  // Fechar dropdown ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Limpar pesquisa ao fechar
  React.useEffect(() => {
    if (!open) {
      setSearchTerm("");
    }
  }, [open]);

  const handleSelect = (selectedValue) => {
    onValueChange(selectedValue);
    setOpen(false);
    setSearchTerm("");
  };

  // Navegação por teclado
  const handleKeyDown = (e) => {
    if (!open) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative w-full">
      <Button
        ref={buttonRef}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full justify-between text-left font-normal"
      >
        <span className="truncate flex-1 pr-2" title={value}>
          {value || placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg"
        >
          {/* Campo de pesquisa */}
          <div className="p-2 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
          </div>

          {/* Lista de opções */}
          <div className="max-h-64 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                {searchTerm ? "Nenhuma OSC encontrada" : "Nenhuma opção disponível"}
              </div>
            ) : (
              <div className="py-1">
                {filteredOptions.map((option, index) => {
                  const isAllOption = option === "Todas as OSCs";
                  const isSelected = value === option;
                  const isHighlighted = index === highlightedIndex;
                  
                  return (
                    <button
                      key={option}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
                        isHighlighted ? "bg-slate-100" : ""
                      } ${isSelected ? "bg-blue-50" : ""} ${
                        isAllOption ? "border-b border-slate-200" : ""
                      }`}
                    >
                      <span 
                        className={`truncate flex-1 pr-2 ${isAllOption ? "font-semibold text-slate-900" : ""}`}
                        title={option}
                      >
                        {option}
                      </span>
                      {isSelected && (
                        <Check className="h-4 w-4 text-blue-600 shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
